import { Box, Chip, IconButton, TextField, Typography } from "@mui/material";
import { AddOutlined, Close, Star } from "@mui/icons-material";
import { Autocomplete } from "@mui/material";
import useFieldCustomize from "components/playbook/hooks/useFieldCustomize";
import { FC } from "react";
import { Control, Controller, UseFormSetValue, UseFormUnregister } from "react-hook-form";
import { ConditionRuleDef, FieldOptionDef, StarOptions, genUid, useStyles } from "../common";

const ConditionRule: FC<{
  fieldId: string;
  cr: ConditionRuleDef;
  control: Control;
  setValue: UseFormSetValue<any>;
}> = ({ fieldId, cr, control, setValue }) => {
  const { setFieldStateList, getConditionOptions, getFieldState } = useFieldCustomize();

  const crState = getFieldState(cr.fieldId);

  const optionList = getConditionOptions(fieldId, cr.ruleId);
  const options = optionList.map((field) => ({
    fieldId: field.id,
    fieldLabel: field.label.value,
  }));

  const handleFieldChange = (
    value: {
      fieldId: string;
      fieldLabel: string;
    } | null
  ) => {
    setValue(cr.ruleId, value?.fieldId);
    // update dependsBy
    const originFieldId = (getFieldState(fieldId)?.conditions ?? [{ fieldId: "" }])[0].fieldId;
    setFieldStateList((items) =>
      items.map((item) =>
        item.id === originFieldId
          ? {
              ...item,
              dependsBy: [...(item.dependsBy ?? []).filter((id) => id !== fieldId)],
            }
          : item
      )
    );
    setFieldStateList((items) =>
      items.map((item) =>
        item.id === value?.fieldId
          ? { ...item, dependsBy: [...(item.dependsBy ?? []), fieldId] }
          : item
      )
    );

    setFieldStateList((items) =>
      items.map((item) =>
        item.id === fieldId
          ? {
              ...item,
              conditions: [
                ...(item.conditions ?? []).map((o) =>
                  o.ruleId === cr.ruleId
                    ? { ...o, fieldId: value?.fieldId ?? "", fieldValue: [] }
                    : o
                ),
              ],
            }
          : item
      )
    );
  };

  const valueOptions = cr.fieldId === "" ? [] : (getFieldState(cr.fieldId)?.options ?? []);

  const handleValueChange = (value: FieldOptionDef[]) => {
    setValue(`${cr.ruleId}-values`, value);
    setFieldStateList((items) =>
      items.map((item) =>
        item.id === fieldId
          ? {
              ...item,
              conditions: [
                ...(item.conditions ?? []).map((o) =>
                  o.ruleId === cr.ruleId ? { ...o, fieldValue: [...value] } : o
                ),
              ],
            }
          : item
      )
    );
  };

  return (
    <>
      <Box>
        <Controller
          name={cr.ruleId}
          control={control}
          render={({ field, fieldState }) => (
            <Autocomplete
              {...field}
              fullWidth={true}
              size="small"
              style={{ background: "#fff" }}
              options={options}
              getOptionLabel={(option) => option.fieldLabel}
              isOptionEqualToValue={(o, v) => o.fieldId === v.fieldId}
              onChange={(e, value) => handleFieldChange(value)}
              value={
                options.find((o) => o.fieldId === cr.fieldId) ?? {
                  fieldId: "",
                  fieldLabel: "",
                }
              }
              disableClearable={true}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    margin="normal"
                    variant="outlined"
                    size={"small"}
                    style={{ backgroundColor: "#fff", marginTop: 5 }}
                    autoFocus={false}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    placeholder="Please select conditional rule field."
                  />
                );
              }}
            />
          )}
          rules={{
            required: "This field is required.",
          }}
        />
      </Box>
      <Box style={{ display: "flex", flexDirection: "row" }}>
        <Autocomplete
          options={["in"]}
          value={"in"}
          disabled={true}
          style={{ marginRight: 8, width: 70 }}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                margin="normal"
                variant="outlined"
                size={"small"}
                style={{
                  backgroundColor: "#ededed",
                  color: "#898989",
                  marginTop: 5,
                }}
                autoFocus={false}
              />
            );
          }}
        />
        {crState?.fieldSubType === "lov" ? (
          <Controller
            name={`${cr.ruleId}-values`}
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete
                {...field}
                multiple={true}
                fullWidth={true}
                size="small"
                style={{ background: "#fff" }}
                options={StarOptions}
                value={StarOptions.filter((o) => cr.fieldValue.includes(o))}
                getOptionLabel={(o) => o.optionValue}
                isOptionEqualToValue={(o, v) => o.optionId === v.optionId}
                disableClearable={true}
                onChange={(e, value) => handleValueChange(value)}
                renderOption={(props, option) => (
                  <li {...props}>
                    <IconButton size="small">
                      {Array.from(Array(Number(option.optionValue))).map((v, idx) => (
                        <Star
                          key={idx}
                          style={{ fontSize: 12, color: "#FFCC00" }}
                        />
                      ))}
                    </IconButton>
                  </li>
                )}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      margin="normal"
                      variant="outlined"
                      size={"small"}
                      style={{ backgroundColor: "#fff", marginTop: 5 }}
                      autoFocus={false}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      placeholder="Please select conditional rule values."
                    />
                  );
                }}
                limitTags={1}
                renderTags={(value, getTagProps) =>
                  value.map((o, idx) => (
                    <Chip
                      key={idx}
                      icon={
                        <>
                          {Array.from(Array(Number(o.optionValue))).map((v, idx) => (
                            <Star
                              key={idx}
                              style={{ fontSize: 12, color: "#FFCC00" }}
                            />
                          ))}
                        </>
                      }
                      {...getTagProps({ index: idx })}
                      size={"small"}
                      style={{ margin: 1, width: "auto", paddingLeft: 5 }}
                    />
                  ))
                }
              />
            )}
            rules={{
              required: "This field is required.",
            }}
          />
        ) : (
          <Controller
            name={`${cr.ruleId}-values`}
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete
                {...field}
                multiple={true}
                fullWidth={true}
                size="small"
                style={{ background: "#fff" }}
                options={valueOptions}
                value={cr.fieldValue}
                getOptionLabel={(o) => o.optionValue}
                isOptionEqualToValue={(o, v) => o.optionId === v.optionId}
                disableClearable={true}
                onChange={(e, value) => handleValueChange(value)}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      margin="normal"
                      variant="outlined"
                      size={"small"}
                      style={{ backgroundColor: "#fff", marginTop: 5 }}
                      autoFocus={false}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      placeholder="Please select conditional rule values."
                    />
                  );
                }}
              />
            )}
            rules={{
              required: "This field is required.",
            }}
          />
        )}
      </Box>
    </>
  );
};

const ConditionRuleEditor: FC<{
  fieldId: string;
  control: Control;
  setValue: UseFormSetValue<any>;
  unregister: UseFormUnregister<any>;
}> = ({ fieldId, control, setValue, unregister }) => {
  const classes = useStyles();
  const { getFieldState, setFieldStateList, isConditionVisiable } = useFieldCustomize();

  const state = getFieldState(fieldId);

  const handleAddRule = () => {
    // toast.error("Please select account(s) first!");
    // return false;

    const ruleId = genUid("rule");
    const cr = {
      ruleId: ruleId,
      fieldId: "",
      fieldValue: [],
    };
    setFieldStateList((items) =>
      items.map((item) =>
        item.id === state?.id
          ? {
              ...item,
              passValidate: false,
              conditions: [...(item.conditions ?? []), cr],
              visible: false,
            }
          : item
      )
    );
  };

  const handleClearRules = () => {
    state?.conditions?.map((cr) => {
      unregister(cr.ruleId);
      setFieldStateList((items) =>
        items.map((item) =>
          item.id === cr.fieldId
            ? {
                ...item,
                dependsBy: [...(item.dependsBy ?? []).filter((d) => d !== fieldId)],
              }
            : item
        )
      );
    });
    setFieldStateList((items) =>
      items.map((item) =>
        item.id === fieldId
          ? {
              ...item,
              conditions: [],
              visible: true,
              passValidate: true,
            }
          : item
      )
    );
  };

  return (
    <>
      {state?.conditions &&
        state.conditions.map((cr) => (
          <div
            key={cr.ruleId}
            style={{
              margin: 8,
              paddingTop: 20,
              border: "1px solid #DEDEDE",
              borderRadius: 4,
              padding: 25,
            }}
          >
            <Box
              style={{
                justifyContent: "center",
                justifyItems: "center",
                display: "flex",
                flexDirection: "row",
              }}
            >
              <Typography
                variant="h6"
                align="center"
                style={{ width: "90%" }}
              >
                Conditional Rule
              </Typography>
              <IconButton
                size="small"
                onClick={handleClearRules}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
            <Typography style={{ fontSize: 12, color: "#A7A7A7" }}>
              Conditional rules, also called branching or skip logic, helps make your form simpler
              and easier to fill out by hiding questions that are not relavant to specific answers.
            </Typography>
            <ConditionRule
              key={cr.ruleId}
              fieldId={fieldId}
              cr={cr}
              control={control}
              setValue={setValue}
            />
          </div>
        ))}
      {isConditionVisiable(fieldId) && (state?.conditions ?? []).length === 0 ? (
        <div style={{ marginTop: 8, marginLeft: 8 }}>
          <AddOutlined style={{ fontSize: 13, color: "#007AA3" }} />
          <span
            className={classes.name}
            onClick={handleAddRule}
          >
            Add Conditional Rule
          </span>
        </div>
      ) : undefined}
    </>
  );
};

export default ConditionRuleEditor;
